import React, { useState, useMemo } from 'react';
import { useData } from '../DataContext';
import { api } from '../services/api';
import { format } from 'date-fns';
import { X } from 'lucide-react';

const Expenses = () => {
  const { expenses = [], loading, refreshExpenses } = useData();
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [viewingExpense, setViewingExpense] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    category: 'Разное'
  });

  const formatAmountInput = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (!numbers) return '';
    return new Intl.NumberFormat('ru-RU').format(numbers);
  };

  const parseFormattedAmount = (value) => {
    return value.replace(/\s/g, '');
  };

  const handleAmountChange = (e) => {
    const formatted = formatAmountInput(e.target.value);
    setFormData({ ...formData, amount: formatted });
  };

  const handleView = (expense) => {
    setViewingExpense(expense);
    setIsEditing(false);
    setFormData({
      description: expense.description,
      amount: formatAmountInput(expense.amount.toString()),
      category: expense.category
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        amount: parseFormattedAmount(formData.amount)
      };

      if (viewingExpense) {
        // Обновление расхода
        try {
          if (typeof api.updateExpense === 'function') {
            await api.updateExpense(viewingExpense._id, submitData);
          } else {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/expenses/${viewingExpense._id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {})
              },
              body: JSON.stringify(submitData)
            });

            if (!response.ok) {
              const text = await response.text().catch(() => null);
              throw new Error(`Сервер вернул ${response.status}: ${text}`);
            }
          }
        } catch (error) {
          console.error('Ошибка обновления расхода:', error);
          throw error;
        }
      } else {
        // Создание нового расхода
        await api.createExpense(submitData);
      }

      setShowModal(false);
      setViewingExpense(null);
      setIsEditing(false);
      setFormData({
        description: '',
        amount: '',
        category: 'Разное'
      });
      await refreshExpenses();
    } catch (error) {
      console.error('Ошибка сохранения расхода:', error);
      alert('Ошибка при сохранении расхода: ' + (error.message || 'Unknown error'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Вы уверены, что хотите удалить этот расход?')) return;

    try {
      const token = localStorage.getItem('token');

      if (typeof api.deleteExpense === 'function') {
        await api.deleteExpense(viewingExpense._id);
      } else {
        const response = await fetch(`/api/expenses/${viewingExpense._id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          }
        });

        if (!response.ok) {
          const text = await response.text().catch(() => null);
          throw new Error(`Ошибка удаления: ${text || response.status}`);
        }
      }

      alert('Расход успешно удален!');
      setShowModal(false);
      setViewingExpense(null);
      await refreshExpenses();
    } catch (error) {
      console.error('Ошибка удаления расхода:', error);
      alert('Ошибка при удалении расхода: ' + (error.message || 'Unknown error'));
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('uz-UZ').format(num);
  };

  const filteredExpenses = useMemo(() =>
    expenses.filter(e =>
      e.description.toLowerCase().includes(search.toLowerCase()) ||
      e.category.toLowerCase().includes(search.toLowerCase())
    ),
    [expenses, search]
  );

  const getCategoryColor = (category) => {
    const colors = {
      'Аренда': 'bg-red-900/50 text-red-300 border border-red-800/30',
      'Зарплата': 'bg-blue-900/50 text-blue-300 border border-blue-800/30',
      'Материалы': 'bg-green-900/50 text-green-300 border border-green-800/30',
      'Коммунальные': 'bg-yellow-900/50 text-yellow-300 border border-yellow-800/30',
      'Реклама': 'bg-purple-900/50 text-purple-300 border border-purple-800/30',
      'Разное': 'bg-gray-700/50 text-gray-300 border border-gray-600/30'
    };
    return colors[category] || colors['Разное'];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Загрузка данных...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <h1 className="text-3xl font-bold text-white">Расходы</h1>
        <button
          onClick={() => {
            setViewingExpense(null);
            setIsEditing(false);
            setFormData({
              description: '',
              amount: '',
              category: 'Разное'
            });
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-800 font-semibold transition-all shadow-md"
        >
          <span>Добавить расход</span>
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4">
          <input
            type="text"
            placeholder="Поиск по описанию или категории..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-white placeholder-gray-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-750">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Дата
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Описание
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Категория
              </th>
              <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Сумма
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {filteredExpenses.map((expense) => (
              <tr 
                key={expense._id} 
                className="hover:bg-gray-750 transition-colors cursor-pointer"
                onClick={() => handleView(expense)}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                  {format(new Date(expense.date), 'dd.MM.yyyy HH:mm')}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-white">
                  {expense.description}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCategoryColor(expense.category)}`}>
                    {expense.category}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-400">
                  {formatNumber(expense.amount)} сўм
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden space-y-3">
        {filteredExpenses.map((expense) => (
          <div 
            key={expense._id} 
            className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl p-4 hover:bg-gray-750/50 transition-all cursor-pointer"
            onClick={() => handleView(expense)}
          >
            <div className="flex justify-between items-start mb-3">
              <div className="flex-1">
                <h3 className="font-semibold text-base text-white mb-1">{expense.description}</h3>
                <p className="text-xs text-gray-400">{format(new Date(expense.date), 'dd.MM.yyyy HH:mm')}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-lg text-xs font-medium ml-2 ${getCategoryColor(expense.category)}`}>
                {expense.category}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Сумма:</span>
              <span className="font-bold text-red-400">{formatNumber(expense.amount)} сўм</span>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredExpenses.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-400">Расходы не найдены</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {viewingExpense ? 'Информация о расходе' : 'Новый расход'}
                </h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setViewingExpense(null);
                    setIsEditing(false);
                  }}
                  className="text-gray-400 hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {viewingExpense && !isEditing ? (
                <div className="space-y-4">
                  <div className="bg-gray-700/30 rounded-lg p-4 space-y-3">
                    <div>
                      <span className="text-gray-400 text-sm">Дата:</span>
                      <p className="text-white font-medium">{format(new Date(viewingExpense.date), 'dd.MM.yyyy HH:mm')}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Описание:</span>
                      <p className="text-white font-medium">{viewingExpense.description}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Категория:</span>
                      <p className="text-white font-medium">{viewingExpense.category}</p>
                    </div>
                    <div>
                      <span className="text-gray-400 text-sm">Сумма:</span>
                      <p className="text-red-400 font-bold text-lg">{formatNumber(viewingExpense.amount)} сўм</p>
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex-1 btn-primary"
                    >
                      Изменить
                    </button>
                    <button
                      onClick={handleDelete}
                      className="flex-1 btn-secondary text-red-400 hover:text-red-300"
                    >
                      Удалить
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Описание
                    </label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      required
                      placeholder="Например: Аренда помещения"
                      className="input-field"
                    />
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Категория
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({...formData, category: e.target.value})}
                      className="input-field"
                    >
                      <option value="Разное">Разное</option>
                      <option value="Аренда">Аренда</option>
                      <option value="Зарплата">Зарплата</option>
                      <option value="Материалы">Материалы</option>
                      <option value="Коммунальные">Коммунальные</option>
                      <option value="Реклама">Реклама</option>
                    </select>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Сумма (сўм)
                    </label>
                    <input
                      type="text"
                      value={formData.amount}
                      onChange={handleAmountChange}
                      required
                      placeholder="0"
                      className="input-field"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (viewingExpense) {
                          setIsEditing(false);
                        } else {
                          setShowModal(false);
                        }
                      }}
                      className="flex-1 btn-secondary"
                      disabled={submitting}
                    >
                      Отмена
                    </button>
                    <button
                      type="submit"
                      className="flex-1 btn-primary"
                      disabled={submitting}
                    >
                      {submitting ? 'Сохранение...' : (viewingExpense ? 'Сохранить' : 'Создать')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;